import { Component, inject, signal, computed, effect } from '@angular/core';
import { SlicePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { AppVersionService } from '../../services/app-version.service';
import { ToastFeedbackService } from '../../services/toast-feedback.service';
import { formatUserFacingError } from '../../utils/user-facing-error.util';
import { environment } from '../../../environments/environment';
import { PathwaysLogoComponent } from '../pathways-logo/pathways-logo.component'; // PAT-123

interface ReleaseNotes {
  version: string;
  notes_markdown: string;
  released_at?: string;
}

// PAT-203 — post-maintenance release notes modal; persists dismiss via mark-seen API.
@Component({
  selector: 'app-whats-new-modal',
  standalone: true,
  imports: [SlicePipe, PathwaysLogoComponent],
  templateUrl: './whats-new-modal.component.html',
  styleUrl: './whats-new-modal.component.scss',
})
export class WhatsNewModalComponent {
  private readonly auth = inject(AuthService);
  private readonly http = inject(HttpClient);
  private readonly versionSvc = inject(AppVersionService);
  private readonly toast = inject(ToastFeedbackService);
  private readonly apiBase = environment.chatApiBaseUrl.replace(/\/api\/chat.*$/, '');

  readonly visible = signal<boolean>(false);
  readonly dismissing = signal<boolean>(false);
  readonly notes = signal<ReleaseNotes | null>(null);
  readonly previewHtml = computed(() => {
    const n = this.notes();
    return n ? this.parseMarkdown(n.notes_markdown) : '';
  });

  private lastCheckedEmail: string | null = null;

  constructor() {
    effect(() => {
      const email = this.auth.user()?.email?.trim();
      if (!email) {
        this.lastCheckedEmail = null;
        return;
      }
      if (this.versionSvc.maintenanceActive()) return;
      if (this.lastCheckedEmail === email) return;
      this.lastCheckedEmail = email;
      void this.tryShow();
    });
  }

  private async tryShow(): Promise<void> {
    if (this.versionSvc.maintenanceActive()) return;
    const data = await this.fetchLatestUnseenRelease();
    if (!data) return;
    this.notes.set(data);
    this.visible.set(true);
  }

  async dismiss(): Promise<void> {
    const version = this.notes()?.version;
    if (!version || this.dismissing()) return;

    this.dismissing.set(true);
    try {
      const saved = await this.markReleaseSeen(version);
      if (!saved) {
        await this.toast.show(
          formatUserFacingError(null, 'Could not save that you viewed this release'),
          { variant: 'error' },
        );
        return;
      }
      this.visible.set(false);
    } finally {
      this.dismissing.set(false);
    }
  }

  private async fetchLatestUnseenRelease(): Promise<ReleaseNotes | null> {
    try {
      const res = await firstValueFrom(
        this.http.get<ReleaseNotes>(`${this.apiBase}/api/system/release-notes/unseen`, {
          observe: 'response',
        }),
      );
      if (res.status === 204) return null;
      const data = res.body;
      if (data?.notes_markdown) return data;
    } catch {
      // network error — modal stays hidden
    }
    return null;
  }

  private async markReleaseSeen(version: string): Promise<boolean> {
    try {
      await firstValueFrom(
        this.http.post(`${this.apiBase}/api/system/release-notes/mark-seen`, { version }),
      );
      return true;
    } catch {
      return false;
    }
  }

  private parseMarkdown(md: string): string {
    if (!md.trim()) return '';
    let html = md
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^# (.+)$/gm, '<h1>$1</h1>')
      .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code>$1</code>')
      .replace(/^[-*] (.+)$/gm, '<li>$1</li>')
      .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
    html = html.replace(/((<li>[^\n]*<\/li>\n?)+)/g, m => '<ul>' + m.replace(/\n/g, '') + '</ul>');
    html = html.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br>');
    return `<p>${html}</p>`;
  }
}
